import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import toast from "react-hot-toast";
import Loader from "../../assets/Loader.gif";
import Searchbar from "../../components/Search";
import useApiGet from "../hooks/getData";
import { useSelector } from "react-redux";

function RoomThemes() {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [mode, setMode] = useState("image");

  const [form, setForm] = useState({
    name: "",
    coins: "",
    sort_order: "",
    is_active: true,
    image: null,
    colors: "",
  });

  const { getData } = useApiGet();
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);
  const token = Cookies.get("Snapta_Admin_Token");
  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchThemes = async () => {
    setLoading(true);
    try {
      const res = await getData("/rooms/themes/admin-list");
      setThemes(res?.data || []);
    } catch (err) {
      toast.error("Failed to load themes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({
      name: "",
      coins: "",
      sort_order: "",
      is_active: true,
      image: null,
      colors: "",
    });
    setMode("image");
  };

  const openAddModal = () => {
    setEditingTheme(null);
    resetForm();
    setOpen(true);
  };

  const openEditModal = (theme) => {
    setEditingTheme(theme);
    setForm({
      name: theme.name,
      coins: theme.coins,
      sort_order: theme.sort_order,
      is_active: theme.is_active,
      image: null,
      colors: (theme.colors || []).map((c) => c.replace("#", "")).join(", "),
    });
    setMode(theme.image_url ? "image" : "colors");
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!form.name || form.coins === "") {
      toast.error("Please fill name and coins");
      return;
    }
    if (mode === "colors" && form.colors.trim() === "") {
      toast.error("Please enter at least 2 gradient colors");
      return;
    }
    if (mode === "image" && !form.image && !editingTheme?.image_url) {
      toast.error("Please upload an image, or switch to gradient colors");
      return;
    }

    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("coins", form.coins);
    if (form.sort_order !== "") fd.append("sort_order", form.sort_order);
    fd.append("is_active", form.is_active ? "1" : "0");

    if (mode === "image" && form.image) {
      fd.append("image", form.image);
    }
    if (mode === "colors" && form.colors.trim() !== "") {
      fd.append("colors", form.colors);
    }

    setSaving(true);
    try {
      if (editingTheme) {
        fd.append("id", editingTheme.id);
        await axios.post(`${apiUrl}/rooms/themes/edit`, fd, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        });
        toast.success("Theme updated successfully");
      } else {
        await axios.post(`${apiUrl}/rooms/themes/add`, fd, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        });
        toast.success("Theme added successfully");
      }
      handleClose();
      fetchThemes();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this theme? This cannot be undone.")) return;
    try {
      await axios.post(`${apiUrl}/rooms/themes/delete`, { id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Theme deleted");
      fetchThemes();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  const handleToggleActive = async (theme) => {
    try {
      const fd = new FormData();
      fd.append("id", theme.id);
      fd.append("is_active", theme.is_active ? "0" : "1");
      await axios.post(`${apiUrl}/rooms/themes/edit`, fd, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      setThemes((prev) =>
        prev.map((t) => (t.id === theme.id ? { ...t, is_active: !t.is_active } : t))
      );
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const renderPreview = (theme) => {
    if (theme.image_url) {
      return <img src={theme.image_url} alt={theme.name} className="object-cover w-12 h-12 rounded-md" />;
    }
    if (theme.colors?.length >= 2) {
      return (
        <div
          className="w-12 h-12 rounded-md"
          style={{ background: `linear-gradient(135deg, ${theme.colors.join(", ")})` }}
        />
      );
    }
    return <span className="text-xs">—</span>;
  };

  return (
    <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
      <Searchbar />
      <div className="px-4 pb-10 xl:px-6">

        <div className="flex items-center justify-between py-3">
          <h2 className="text-xl font-semibold font-poppins text-[#000000] dark:text-darkText pt-3">
            Room Themes
          </h2>
          <button
            onClick={openAddModal}
            style={{ backgroundColor: "#593A99" }}
            className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg font-poppins shadow-sm hover:opacity-90"
          >
            + Add Theme
          </button>
        </div>

        <div className="mt-4 overflow-x-auto bg-white border border-[#F2F2F2] rounded-xl dark:bg-primary dark:border-[#1F1F1F]">
          <table className="w-full text-sm text-left font-poppins">
            <thead>
              <tr className="text-[#000000] dark:text-darkText border-b border-[#F2F2F2] dark:border-[#1F1F1F]">
                <th className="px-4 py-3">Preview</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Coins</th>
                <th className="px-4 py-3">Sort Order</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                    Loading...
                  </td>
                </tr>
              ) : themes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-[#5B5B5B] dark:text-[#AAAAAA]">
                    No themes found. Click "Add Theme" to create one.
                  </td>
                </tr>
              ) : (
                themes.map((theme) => (
                  <tr key={theme.id} className="border-b border-[#F2F2F2] dark:border-[#1F1F1F] text-[#5B5B5B] dark:text-[#AAAAAA]">
                    <td className="px-4 py-3">{renderPreview(theme)}</td>
                    <td className="px-4 py-3">{theme.name}</td>
                    <td className="px-4 py-3">{theme.slug}</td>
                    <td className="px-4 py-3">
                      {theme.is_free ? (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-md">Free</span>
                      ) : (
                        `🪙 ${theme.coins}`
                      )}
                    </td>
                    <td className="px-4 py-3">{theme.sort_order}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleActive(theme)}
                        className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          theme.is_active
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : "bg-red-100 text-red-700 hover:bg-red-200"
                        }`}
                      >
                        {theme.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openEditModal(theme)}
                        className="px-3 py-1 mr-2 text-xs bg-[#F2F2F2] dark:bg-[#1F1F1F] dark:text-darkText rounded-md"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(theme.id)}
                        className="px-3 py-1 text-xs text-white bg-red-500 rounded-md"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Add / Edit Modal */}
        <Dialog open={open} onClose={handleClose} fullWidth className="custom-dialog">
          <DialogContent className="flex flex-col gap-4 dark:bg-primary">
            <h2 className="text-xl text-center font-poppins text-[#000000] dark:text-darkText">
              {editingTheme ? "Edit Theme" : "Add Theme"}
            </h2>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
                placeholder="e.g. Sunset Glow"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">Coins (0 = free)</label>
              <input
                type="number"
                min={0}
                value={form.coins}
                onChange={(e) => setForm({ ...form, coins: e.target.value })}
                className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
                placeholder="e.g. 500"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">Sort Order (optional)</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
                placeholder="e.g. 1"
              />
            </div>

            <hr className="border-[#F2F2F2] dark:border-[#1F1F1F]" />

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode("image")}
                className={`px-3 py-1 text-xs rounded-lg font-poppins ${
                  mode === "image" ? "bg-[#593A99] text-white" : "bg-[#F2F2F2] dark:bg-[#1F1F1F] text-[#5B5B5B] dark:text-[#AAAAAA]"
                }`}
              >
                Image
              </button>
              <button
                type="button"
                onClick={() => setMode("colors")}
                className={`px-3 py-1 text-xs rounded-lg font-poppins ${
                  mode === "colors" ? "bg-[#593A99] text-white" : "bg-[#F2F2F2] dark:bg-[#1F1F1F] text-[#5B5B5B] dark:text-[#AAAAAA]"
                }`}
              >
                Gradient Colors
              </button>
            </div>

            {mode === "image" ? (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">Theme Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setForm({ ...form, image: e.target.files[0] })}
                  className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText"
                />
                {editingTheme?.image_url && !form.image && (
                  <img src={editingTheme.image_url} alt="current" className="object-cover w-16 h-16 mt-1 rounded-md" />
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
                  Gradient Colors (comma separated hex, min 2 — e.g. FF5733, 33FF57)
                </label>
                <input
                  type="text"
                  value={form.colors}
                  onChange={(e) => setForm({ ...form, colors: e.target.value })}
                  className="border border-gray-400 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-1 focus:ring-gray-600 dark:bg-primary dark:text-darkText"
                  placeholder="FF5733, 33FF57"
                />
                {form.colors.trim() !== "" && (
                  <div
                    className="w-full h-10 mt-1 rounded-md"
                    style={{
                      background: `linear-gradient(135deg, ${form.colors.split(",").map((c) => "#" + c.trim().replace("#", "")).join(", ")})`,
                    }}
                  />
                )}
              </div>
            )}

            <label className="flex items-center gap-2 text-sm font-poppins text-[#5B5B5B] dark:text-darkText">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Active (visible to users)
            </label>
          </DialogContent>

          <DialogContent className="flex justify-center gap-4 pb-4 dark:bg-primary">
            <button
              className="w-[150px] py-2 border border-header text-[#3A3333] dark:text-darkText rounded-lg"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              style={{ backgroundColor: "#0D9947" }}
              className="w-[150px] py-2 text-white rounded-lg"
            >
              {saving ? (
                <div className="flex justify-center py-1">
                  <img src={Loader} alt="loader" height={10} width={10} />
                </div>
              ) : (
                "Save"
              )}
            </button>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
}

export default RoomThemes;